import type { PlanogramShelfDividers } from "@CommonTypes/merchflow/pog/shelf";

/** The PSA fixture's divider fields, in raw PSA units (inches or cm). */
export interface PsaFixtureDividerFields {
  dividerWidth: number;
  dividerHeight: number;
  dividerDepth: number;
  dividerAtStart: number;
  dividerAtEnd: number;
  dividersBetweenFacings: number;
}

const toFiniteNumber = (value: unknown): number => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const toHundredths = (value: unknown): number => Math.round(toFiniteNumber(value) * 100);

/**
 * A PSA fixture's divider fields as a pog shelf's `dividers`, or null when the
 * fixture states no divider at all. Lengths become the pog's x100 integers; the
 * three PSA switches are booleans.
 */
export const shelfDividersFromPsaFixture = (fields: {
  divider_width?: unknown;
  divider_height?: unknown;
  divider_depth?: unknown;
  divider_at_start?: unknown;
  divider_at_end?: unknown;
  dividers_between_facings?: unknown;
}): PlanogramShelfDividers | null => {
  const dividers: PlanogramShelfDividers = {
    width: toHundredths(fields.divider_width),
    height: toHundredths(fields.divider_height),
    depth: toHundredths(fields.divider_depth),
    atStart: toFiniteNumber(fields.divider_at_start) !== 0,
    atEnd: toFiniteNumber(fields.divider_at_end) !== 0,
    numberOfDividers:
      toFiniteNumber(fields.dividers_between_facings) !== 0 ? "dividers_between_facings" : 0,
  };
  const statesAnything =
    dividers.width !== 0 ||
    dividers.height !== 0 ||
    dividers.depth !== 0 ||
    dividers.atStart ||
    dividers.atEnd ||
    dividers.numberOfDividers !== 0;
  return statesAnything ? dividers : null;
};

/**
 * A pog shelf's `dividers` as the PSA fixture record's divider fields (raw PSA
 * units). A shelf without dividers, or one the pogger left without them
 * (`active: false`), writes zeros. A fixed count has no PSA field of its own
 * and is written as "not between facings".
 */
export const psaFixtureDividerFields = (
  dividers: PlanogramShelfDividers | null | undefined,
): PsaFixtureDividerFields => {
  if (!dividers || dividers.active === false) {
    return {
      dividerWidth: 0,
      dividerHeight: 0,
      dividerDepth: 0,
      dividerAtStart: 0,
      dividerAtEnd: 0,
      dividersBetweenFacings: 0,
    };
  }
  const toPsaLength = (hundredths: number) => Math.round(toFiniteNumber(hundredths)) / 100;
  return {
    dividerWidth: toPsaLength(dividers.width),
    dividerHeight: toPsaLength(dividers.height),
    dividerDepth: toPsaLength(dividers.depth),
    dividerAtStart: dividers.atStart ? 1 : 0,
    dividerAtEnd: dividers.atEnd ? 1 : 0,
    dividersBetweenFacings: dividers.numberOfDividers === "dividers_between_facings" ? 1 : 0,
  };
};

type ShelfDividersLike = PlanogramShelfDividers | null | undefined;

/**
 * Dividers that take shelf space: a positive width, between facings or a
 * positive fixed count, and not switched off by the pogger (`active: false`).
 */
export const getActiveShelfDividers = (dividers: ShelfDividersLike) => {
  if (!dividers || dividers.active === false || !(toFiniteNumber(dividers.width) > 0)) return null;
  if (dividers.numberOfDividers === "dividers_between_facings") return dividers;
  return toFiniteNumber(dividers.numberOfDividers) > 0 ? dividers : null;
};

/** The divider width charged after every facing column, as the pogger charges it; 0 when none. */
export const getBetweenFacingsDividerWidth = (dividers: ShelfDividersLike): number => {
  const active = getActiveShelfDividers(dividers);
  return active?.numberOfDividers === "dividers_between_facings" ? toFiniteNumber(active.width) : 0;
};

/** A strip of a board no product may occupy, in the board's own x (a `ShelfDeadSpan`). */
interface DividerDeadSpanLike {
  x: number;
  width: number;
}

/**
 * What the divider rule reads off an item's shelf. A pog's `PlanogramShelf` has
 * all of it; a shelf with only `dividers` is a shelf no pole cuts.
 */
export interface DividerShelfLike {
  dividers?: PlanogramShelfDividers | null;
  width?: number;
  deadSpans?: readonly DividerDeadSpanLike[] | null;
  /** The bay's shelves: their strips cut this board too, as the pogger cuts the bay. */
  bay?: {
    shelves?: readonly { width?: number; deadSpans?: readonly DividerDeadSpanLike[] | null }[];
  } | null;
}

export interface DividerItemLike {
  getWidth: () => number;
  getHorizontalFacings: () => number;
  getBlockWidth: () => number;
  getXCappingWidth: () => number;
  shelf?: DividerShelfLike | null;
}

/** A stretch of a board between poles, in x from the run's left edge. */
export interface ShelfPiece {
  x: number;
  width: number;
}

/** An item's share of a divided shelf, in x relative to the left edge of its slot. */
export interface ShelfItemDividerSlot {
  /** What the item takes on the shelf: product, caps and every divider the rule gives it. */
  width: number;
  /** Where the product's first facing column starts. */
  productX: number;
  /** From the first facing's left edge to the caps' right edge, dividers between its facings included. */
  productWidth: number;
  /** Left edge of each facing column. */
  columnXs: number[];
  /** Left edge of each divider. */
  dividerXs: number[];
  /** The hardware drawn at `dividerXs`; null when the item has none. */
  dividers: PlanogramShelfDividers | null;
  /**
   * On a shelf a pole cuts, the piece of the board the item stands in: the run
   * lays out piece by piece (`getShelfItemXOffsets`' `pieces`). Undefined on a
   * shelf no pole cuts.
   */
  piece?: ShelfPiece;
}

const dividerFreeSlot = (item: DividerItemLike): ShelfItemDividerSlot => {
  const width = item.getWidth();
  const columns = Math.max(0, item.getHorizontalFacings());
  return {
    width: item.getBlockWidth() + item.getXCappingWidth(),
    productX: 0,
    productWidth: item.getBlockWidth() + item.getXCappingWidth(),
    columnXs: Array.from({ length: columns }, (_, column) => column * width),
    dividerXs: [],
    dividers: null,
  };
};

/** Lengths closer than this are equal: pog widths are x100 floats. */
const LENGTH_EPSILON = 1e-6;

/** A board's free stretches between its own strips, left to right, in its own x. */
const getBoardPieces = (shelf: DividerShelfLike): ShelfPiece[] => {
  const width = toFiniteNumber(shelf.width);
  const strips = (shelf.deadSpans ?? [])
    .map((span) => ({
      start: Math.max(0, toFiniteNumber(span.x)),
      end: Math.min(width, toFiniteNumber(span.x) + toFiniteNumber(span.width)),
    }))
    .filter(({ start, end }) => end > start)
    .sort((left, right) => left.start - right.start);
  const pieces: ShelfPiece[] = [];
  let x = 0;
  for (const { start, end } of strips) {
    if (start > x + LENGTH_EPSILON) pieces.push({ x, width: start - x });
    x = Math.max(x, end);
  }
  if (width > x + LENGTH_EPSILON) pieces.push({ x, width: width - x });
  return pieces;
};

/**
 * The piece that carries a fixed count: the one holding the board's widest
 * stretch between the bay's cuts (every strip edge of every shelf in the bay),
 * the leftmost on a tie, as the pogger's `_split_bay` picks it.
 */
const getFixedCountPieceIndex = (shelf: DividerShelfLike, pieces: readonly ShelfPiece[]) => {
  const width = toFiniteNumber(shelf.width);
  const shelves = shelf.bay?.shelves?.length ? shelf.bay.shelves : [shelf];
  const bayWidth = Math.max(width, ...shelves.map((member) => toFiniteNumber(member.width)));
  const cuts = Array.from(
    new Set(
      shelves.flatMap((member) =>
        (member.deadSpans ?? []).flatMap((span) => [
          toFiniteNumber(span.x),
          toFiniteNumber(span.x) + toFiniteNumber(span.width),
        ]),
      ),
    ),
  )
    .filter((edge) => edge > 0 && edge < bayWidth)
    .sort((left, right) => left - right);
  const edges = [0, ...cuts, bayWidth];
  let carrying = 0;
  let widest = -Infinity;
  for (let edge = 0; edge + 1 < edges.length; edge += 1) {
    const lo = edges[edge];
    const stretch = Math.min(edges[edge + 1], width) - lo;
    const piece = pieces.findIndex(
      (candidate) => lo >= candidate.x - LENGTH_EPSILON && lo < candidate.x + candidate.width,
    );
    if (piece >= 0 && stretch > widest + LENGTH_EPSILON) {
      carrying = piece;
      widest = stretch;
    }
  }
  return carrying;
};

type ActiveShelfDividers = NonNullable<ReturnType<typeof getActiveShelfDividers>>;

/** Where an item's own dividers start and end: its shelf, or its piece of a board a pole cuts. */
interface DividerSegment {
  /** The hardware, or null when this stretch of the board carries none. */
  dividers: ActiveShelfDividers | null;
  first: boolean;
  last: boolean;
  atStart: boolean;
  atEnd: boolean;
  /** Counts a fixed count's dividers given out so far. */
  key: object;
  piece?: ShelfPiece;
}

/**
 * The segments of one shelf's items when a pole cuts the shelf (two or more
 * free pieces), as the pogger charges the pieces (`api/dead_spans.py`
 * `_piece_dividers`, `core.divider_shelf_charge`, each piece charged only when
 * it holds a column). The pogger has no x, so the items go into the pieces
 * left to right, each as far left as what the pogger charges its piece lets it:
 * - between facings: the start divider on the first piece, the end flag on the
 *   last; every other piece ends at a pole, where nothing stands, so its last
 *   column's divider is handed back;
 * - a fixed count: the count and both end flags all on the piece holding the
 *   board's widest stretch, nothing on the others, and none at all when that
 *   piece holds no product.
 * Null when no pole cuts the shelf.
 */
const getPoleSegments = (
  shelf: DividerShelfLike,
  dividers: ActiveShelfDividers,
  items: readonly DividerItemLike[],
): DividerSegment[] | null => {
  const pieces = getBoardPieces(shelf);
  if (pieces.length < 2) return null;

  const lastPiece = pieces.length - 1;
  const dividerWidth = toFiniteNumber(dividers.width);
  const betweenFacings = dividers.numberOfDividers === "dividers_between_facings";
  const carrying = betweenFacings ? -1 : getFixedCountPieceIndex(shelf, pieces);
  const atStart = (piece: number) => Boolean(dividers.atStart) && piece === 0;
  const atEnd = (piece: number) => (piece === lastPiece ? Boolean(dividers.atEnd) : false);
  const pieceCharge = (piece: number) => {
    if (betweenFacings) {
      return dividerWidth * (Number(atStart(piece)) - (1 - Number(atEnd(piece))));
    }
    if (piece !== carrying) return 0;
    return (
      dividerWidth *
      (toFiniteNumber(dividers.numberOfDividers) +
        Number(Boolean(dividers.atStart)) +
        Number(Boolean(dividers.atEnd)))
    );
  };
  const itemCost = (item: DividerItemLike) =>
    item.getBlockWidth() +
    item.getXCappingWidth() +
    (betweenFacings ? dividerWidth * Math.max(0, item.getHorizontalFacings()) : 0);

  const pieceOf: number[] = [];
  let piece = 0;
  let used = 0;
  let held = 0;
  for (const item of items) {
    const cost = itemCost(item);
    while (piece < lastPiece) {
      const total = used + cost + (held === 0 ? pieceCharge(piece) : 0);
      if (total <= pieces[piece].width + LENGTH_EPSILON) break;
      piece += 1;
      used = 0;
      held = 0;
    }
    used += cost + (held === 0 ? pieceCharge(piece) : 0);
    held += 1;
    pieceOf.push(piece);
  }

  const keys = pieces.map(() => ({}));
  return items.map((_, index) => {
    const own = pieceOf[index];
    return {
      dividers: betweenFacings || own === carrying ? dividers : null,
      first: index === 0 || pieceOf[index - 1] !== own,
      last: index === items.length - 1 || pieceOf[index + 1] !== own,
      atStart: betweenFacings ? atStart(own) : Boolean(dividers.atStart),
      atEnd: betweenFacings ? atEnd(own) : Boolean(dividers.atEnd),
      key: keys[own],
      piece: pieces[own],
    };
  });
};

/** Each item's segment: its shelf's ends, or its piece's on a shelf a pole cuts. */
const getDividerSegments = (items: readonly DividerItemLike[]): (DividerSegment | null)[] => {
  const segments: (DividerSegment | null)[] = [];
  // Strips stop a shelf merging, so only a run of one shelf can have a pole.
  const oneShelf = items.every((item) => item.shelf === items[0]?.shelf);
  let start = 0;
  while (start < items.length) {
    const shelf = items[start].shelf;
    let end = start;
    while (end + 1 < items.length && items[end + 1].shelf === shelf) end += 1;
    const shelfItems = items.slice(start, end + 1);
    const dividers = getActiveShelfDividers(shelf?.dividers);
    const poleSegments =
      dividers && shelf && oneShelf ? getPoleSegments(shelf, dividers, shelfItems) : null;
    shelfItems.forEach((_, offset) => {
      if (!dividers) {
        segments.push(null);
        return;
      }
      segments.push(
        poleSegments?.[offset] ?? {
          dividers,
          first: offset === 0,
          last: offset === shelfItems.length - 1,
          atStart: Boolean(dividers.atStart),
          atEnd: Boolean(dividers.atEnd),
          key: shelf as object,
        },
      );
    });
    start = end + 1;
  }
  return segments;
};

/**
 * Where the dividers go on a run of items standing left to right (one shelf, or
 * a merged group), by the pogger's rule. The pogger charges every shelf (bay
 * and shelf number) its own dividers (`divider_shelf_charge`), so on a merged
 * run each shelf's start and end flags apply at that shelf's own first and last
 * item, not only at the run's outer two. Between facings: one divider after
 * every facing column, the shelf's last one dropped unless `atEnd`, plus one
 * before the shelf's first facing when `atStart`; x-caps stand before a
 * column's divider. A fixed count N: N dividers after the shelf's first N
 * items, plus the shelf's ends that are switched on. On a shelf a pole cuts,
 * each piece between poles is charged as the pogger charges it (DEV-3536,
 * `getPoleSegments`): a piece that ends at a pole has no divider against it.
 * Null when no item's shelf has active dividers, so callers keep their
 * divider-free arithmetic untouched.
 */
export const getShelfItemDividerSlots = (
  items: readonly DividerItemLike[],
): ShelfItemDividerSlot[] | null => {
  if (!items.some((item) => getActiveShelfDividers(item.shelf?.dividers))) return null;

  const slots = items.map(dividerFreeSlot);
  const segments = getDividerSegments(items);
  const fixedCountSeen = new Map<object, number>();

  items.forEach((item, index) => {
    const segment = segments[index];
    if (!segment) return;
    const slot = slots[index];
    if (segment.piece) slot.piece = segment.piece;
    const { dividers } = segment;
    if (!dividers) return;
    const dividerWidth = toFiniteNumber(dividers.width);
    slot.dividers = dividers;

    if (dividers.numberOfDividers === "dividers_between_facings") {
      const columns = slot.columnXs.length;
      if (columns === 0) return;
      const facingWidth = item.getWidth();
      const leading = segment.first && segment.atStart ? dividerWidth : 0;
      const trailing = !(segment.last && !segment.atEnd);
      const pitch = facingWidth + dividerWidth;
      slot.productX = leading;
      slot.columnXs = slot.columnXs.map((_, column) => leading + column * pitch);
      const lastColumnRight = leading + (columns - 1) * pitch + facingWidth;
      const capsWidth = item.getXCappingWidth();
      slot.productWidth = lastColumnRight + capsWidth - leading;
      slot.dividerXs = [
        ...(leading > 0 ? [0] : []),
        ...slot.columnXs.slice(0, -1).map((x) => x + facingWidth),
        ...(trailing ? [lastColumnRight + capsWidth] : []),
      ];
      slot.width = lastColumnRight + capsWidth + (trailing ? dividerWidth : 0);
      return;
    }

    const seen = fixedCountSeen.get(segment.key) ?? 0;
    fixedCountSeen.set(segment.key, seen + 1);
    const count = toFiniteNumber(dividers.numberOfDividers);
    const leading = segment.first && segment.atStart ? dividerWidth : 0;
    let after = seen < count ? 1 : 0;
    if (segment.last) {
      after += Math.max(0, count - seen - 1);
    }
    if (segment.last && segment.atEnd) after += 1;
    slot.productX = leading;
    slot.columnXs = slot.columnXs.map((x) => x + leading);
    const productRight = leading + slot.productWidth;
    slot.dividerXs = [
      ...(leading > 0 ? [0] : []),
      ...Array.from({ length: after }, (_, divider) => productRight + divider * dividerWidth),
    ];
    slot.width = productRight + after * dividerWidth;
  });

  return slots;
};

/** A divider's drawn size: its own height and depth, or a stub and the shelf's depth when unstated. */
export const getDividerDrawSize = (
  dividers: PlanogramShelfDividers,
  shelfDepth: number,
  fallbackHeight: number,
) => ({
  width: toFiniteNumber(dividers.width),
  height: toFiniteNumber(dividers.height) > 0 ? toFiniteNumber(dividers.height) : fallbackHeight,
  depth: toFiniteNumber(dividers.depth) > 0 ? toFiniteNumber(dividers.depth) : shelfDepth,
});

/** Drawn height of a divider whose height is unstated: a short stub, in the pog's x100 units. */
export const DEFAULT_DRAWN_DIVIDER_HEIGHT = 300;

export type ShelfDividerProblem =
  | "products-and-dividers-overflow"
  | "width-not-positive"
  | "width-not-below-shelf"
  | "flag-out-of-range";

/**
 * What makes a shelf's dividers wrong ("Divider width incorrect"). The switches
 * are checked as numbers too: rows imported before DEV-3516 hold 100 for 1.
 */
export const getShelfDividerProblems = (shelf: {
  dividers?: PlanogramShelfDividers | null;
  width: number;
  getItemsWidth: () => number;
  getMergedMerchandisableWidth: () => number;
}): ShelfDividerProblem[] => {
  const { dividers } = shelf;
  if (!dividers) return [];
  const problems: ShelfDividerProblem[] = [];
  const width = toFiniteNumber(dividers.width);
  const enabled =
    dividers.numberOfDividers === "dividers_between_facings" ||
    toFiniteNumber(dividers.numberOfDividers) > 0;

  if (enabled && width <= 0) problems.push("width-not-positive");
  if (width > 0 && width >= shelf.width) problems.push("width-not-below-shelf");
  if ([dividers.atStart, dividers.atEnd].some((flag) => toFiniteNumber(flag) > 1)) {
    problems.push("flag-out-of-range");
  }
  if (
    getActiveShelfDividers(dividers) &&
    shelf.getItemsWidth() > shelf.getMergedMerchandisableWidth()
  ) {
    problems.push("products-and-dividers-overflow");
  }
  return problems;
};
